import React, { useState, useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

// Calculation constants
const WORKING_DAYS_PER_MONTH = 30;
const WORKING_HOURS_PER_DAY = 9;
const DEFAULT_SPF_PERCENTAGE = 22.5;
const DEBOUNCE_MS = 800;

const calcDailySalary = (monthly) => {
  const m = parseFloat(monthly) || 0;
  return m > 0 ? m / WORKING_DAYS_PER_MONTH : 0;
};

const calcHourlySalary = (daily) => {
  return daily > 0 ? daily / WORKING_HOURS_PER_DAY : 0;
};

const calcSPFValue = (monthlySalary, spfPercentage, nationality) => {
  if (nationality !== 'omani') return 0;
  const salary = parseFloat(monthlySalary) || 0;
  const pct = parseFloat(spfPercentage) || DEFAULT_SPF_PERCENTAGE;
  return salary * (pct / 100);
};

const SaveStatusBadge = ({ status }) => {
  if (!status) return null;
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        Saving…
      </span>
    );
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
        Saved ✓
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
        Failed ✗
      </span>
    );
  }
  if (status === 'unsaved') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 102 0V6zm-1 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
        Unsaved
      </span>
    );
  }
  return null;
};

const OutsourceResourcesGrid = forwardRef(({ data, onUpdate, onDelete, searchQuery, onUnsavedCountChange }, ref) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowEdits, setRowEdits] = useState({});
  const [rowStatus, setRowStatus] = useState({});
  const debounceTimers = useRef({});

  const roleOptions = [
    { value: 'senior', label: 'Senior Engineer' },
    { value: 'mid', label: 'Mid-Level Engineer' },
    { value: 'junior', label: 'Junior Engineer' },
    { value: 'lead', label: 'Lead Engineer' },
    { value: 'architect', label: 'Solution Architect' },
  ];

  const nationalityOptions = [
    { value: 'omani', label: 'Omani' },
    { value: 'expat', label: 'Expat' },
  ];

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery?.toLowerCase();
    return data?.filter(item =>
      item?.name?.toLowerCase()?.includes(query) ||
      item?.role?.toLowerCase()?.includes(query) ||
      item?.specialization?.toLowerCase()?.includes(query)
    );
  }, [data, searchQuery]);

  const unsavedCount = useMemo(() => {
    return Object.values(rowStatus)?.filter(s => s === 'unsaved' || s === 'failed')?.length;
  }, [rowStatus]);

  useEffect(() => {
    onUnsavedCountChange?.(unsavedCount);
  }, [unsavedCount, onUnsavedCountChange]);

  useEffect(() => {
    return () => { Object.values(debounceTimers?.current)?.forEach(clearTimeout); };
  }, []);

  const buildSavePayload = (base) => {
    const monthly = parseFloat(base?.monthlySalary) || 0;
    const daily = calcDailySalary(monthly);
    const hourly = calcHourlySalary(daily);
    const nationality = base?.nationality || 'expat';
    const spfPercentage = parseFloat(base?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
    const spfValue = calcSPFValue(monthly, spfPercentage, nationality);
    return {
      ...base,
      monthlySalary: monthly,
      dailySalary: daily,
      hourlySalary: hourly,
      internalCost: daily,
      nationality,
      spfPercentage,
      spfValue,
      healthInsuranceEnabled: base?.healthInsuranceEnabled ?? false,
      healthInsuranceValue: parseFloat(base?.healthInsuranceValue) || 0,
    };
  };

  const triggerAutoSave = useCallback((id, pendingData) => {
    setRowStatus(prev => ({ ...prev, [id]: 'unsaved' }));
    if (debounceTimers?.current?.[id]) clearTimeout(debounceTimers?.current?.[id]);
    debounceTimers.current[id] = setTimeout(async () => {
      setRowStatus(prev => ({ ...prev, [id]: 'saving' }));
      try {
        await onUpdate(buildSavePayload(pendingData));
        setRowStatus(prev => ({ ...prev, [id]: 'saved' }));
        setRowEdits(prev => { const n = { ...prev }; delete n?.[id]; return n; });
        setTimeout(() => {
          setRowStatus(prev => { if (prev?.[id] === 'saved') { const n = { ...prev }; delete n?.[id]; return n; } return prev; });
        }, 3000);
      } catch {
        setRowStatus(prev => ({ ...prev, [id]: 'failed' }));
      }
    }, DEBOUNCE_MS);
  }, [onUpdate]);

  const saveAll = useCallback(async () => {
    const pendingIds = Object.keys(rowEdits);
    if (pendingIds?.length === 0) return;
    await Promise.all(pendingIds?.map(async (id) => {
      if (debounceTimers?.current?.[id]) { clearTimeout(debounceTimers?.current?.[id]); delete debounceTimers?.current?.[id]; }
      setRowStatus(prev => ({ ...prev, [id]: 'saving' }));
      try {
        await onUpdate(buildSavePayload(rowEdits?.[id]));
        setRowStatus(prev => ({ ...prev, [id]: 'saved' }));
        setRowEdits(prev => { const n = { ...prev }; delete n?.[id]; return n; });
        setTimeout(() => {
          setRowStatus(prev => { if (prev?.[id] === 'saved') { const n = { ...prev }; delete n?.[id]; return n; } return prev; });
        }, 3000);
      } catch {
        setRowStatus(prev => ({ ...prev, [id]: 'failed' }));
      }
    }));
  }, [rowEdits, onUpdate]);

  useImperativeHandle(ref, () => ({ saveAll }), [saveAll]);

  const handleEdit = (item) => {
    setEditingId(item?.id);
    setEditData({
      ...item,
      spfPercentage: item?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE,
      nationality: item?.nationality ?? 'expat',
      healthInsuranceEnabled: item?.healthInsuranceEnabled ?? false,
      healthInsuranceValue: item?.healthInsuranceValue ?? 0,
    });
  };

  const handleSave = () => {
    const monthly = parseFloat(editData?.monthlySalary) || 0;
    const daily = calcDailySalary(monthly);
    const hourly = calcHourlySalary(daily);
    const nationality = editData?.nationality || 'expat';
    const spfPercentage = parseFloat(editData?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
    const spfValue = calcSPFValue(monthly, spfPercentage, nationality);
    onUpdate({ ...editData, monthlySalary: monthly, dailySalary: daily, hourlySalary: hourly, internalCost: daily, nationality, spfPercentage, spfValue, healthInsuranceEnabled: editData?.healthInsuranceEnabled ?? false, healthInsuranceValue: parseFloat(editData?.healthInsuranceValue) || 0 });
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => { setEditingId(null); setEditData({}); };

  const handleFieldChange = (item, field, value) => {
    const currentData = rowEdits?.[item?.id] || { ...item };
    let updated = { ...currentData, [field]: value };
    if (field === 'monthlySalary') {
      const monthly = parseFloat(value) || 0;
      const daily = calcDailySalary(monthly);
      const hourly = calcHourlySalary(daily);
      const spfPercentage = parseFloat(updated?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
      const nationality = updated?.nationality || 'expat';
      updated = { ...updated, dailySalary: daily, hourlySalary: hourly, internalCost: daily, spfValue: calcSPFValue(monthly, spfPercentage, nationality) };
    } else if (field === 'nationality') {
      updated = { ...updated, spfValue: calcSPFValue(parseFloat(updated?.monthlySalary) || 0, parseFloat(updated?.spfPercentage) || DEFAULT_SPF_PERCENTAGE, value) };
    } else if (field === 'spfPercentage') {
      updated = { ...updated, spfValue: calcSPFValue(parseFloat(updated?.monthlySalary) || 0, value, updated?.nationality || 'expat') };
    }
    setRowEdits(prev => ({ ...prev, [item?.id]: updated }));
    triggerAutoSave(item?.id, updated);
  };

  const handleRetry = (id) => { if (rowEdits?.[id]) triggerAutoSave(id, rowEdits?.[id]); };

  const handleMonthlySalaryChange = (value) => {
    const monthly = parseFloat(value) || 0;
    const daily = calcDailySalary(monthly);
    const hourly = calcHourlySalary(daily);
    const spfPercentage = parseFloat(editData?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
    const nationality = editData?.nationality || 'expat';
    setEditData(prev => ({ ...prev, monthlySalary: value, dailySalary: daily, hourlySalary: hourly, internalCost: daily, spfValue: calcSPFValue(monthly, spfPercentage, nationality) }));
  };

  const handleNationalityChange = (value) => {
    const monthly = parseFloat(editData?.monthlySalary) || 0;
    const spfPercentage = parseFloat(editData?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
    setEditData(prev => ({ ...prev, nationality: value, spfValue: calcSPFValue(monthly, spfPercentage, value) }));
  };

  const handleSPFPercentageChange = (value) => {
    const monthly = parseFloat(editData?.monthlySalary) || 0;
    const nationality = editData?.nationality || 'expat';
    setEditData(prev => ({ ...prev, spfPercentage: value, spfValue: calcSPFValue(monthly, value, nationality) }));
  };

  const handleSelectRow = (id) => { setSelectedRows(prev => prev?.includes(id) ? prev?.filter(r => r !== id) : [...prev, id]); };
  const handleSelectAll = () => { selectedRows?.length === filteredData?.length ? setSelectedRows([]) : setSelectedRows(filteredData?.map(i => i?.id)); };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'OMR', minimumFractionDigits: 3, maximumFractionDigits: 3 })?.format(value || 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[1600px]">
        <thead>
          <tr className="bg-muted border-b border-border">
            <th className="w-12 px-4 py-3 text-left">
              <input type="checkbox" checked={selectedRows?.length === filteredData?.length && filteredData?.length > 0} onChange={handleSelectAll} className="w-4 h-4 rounded border-border" />
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Name</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Specialization</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Nationality</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Monthly Salary</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Daily Salary <span className="text-xs font-normal text-muted-foreground">(÷30)</span></th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Hourly Salary <span className="text-xs font-normal text-muted-foreground">(÷9)</span></th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">SPF % <span className="text-xs font-normal text-muted-foreground">(Omani only)</span></th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">SPF Value</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Health Ins.</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Health Ins. Value</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Utilization %</th>
            <th className="w-40 px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData?.map((item) => {
            const isEditing = editingId === item?.id;
            const rowEdit = rowEdits?.[item?.id];
            const displayItem = rowEdit || item;

            const dailySalary = displayItem?.dailySalary ?? calcDailySalary(displayItem?.monthlySalary);
            const hourlySalary = displayItem?.hourlySalary ?? calcHourlySalary(dailySalary);
            const nationality = displayItem?.nationality || 'expat';
            const spfPercentage = displayItem?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE;
            const spfValue = displayItem?.spfValue ?? calcSPFValue(displayItem?.monthlySalary, spfPercentage, nationality);
            const healthInsuranceEnabled = displayItem?.healthInsuranceEnabled ?? false;
            const healthInsuranceValue = displayItem?.healthInsuranceValue ?? 0;

            const editDailySalary = editData?.dailySalary ?? calcDailySalary(editData?.monthlySalary);
            const editHourlySalary = editData?.hourlySalary ?? calcHourlySalary(editDailySalary);
            const editNationality = editData?.nationality || 'expat';
            const editSpfValue = editData?.spfValue ?? calcSPFValue(editData?.monthlySalary, editData?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE, editNationality);

            const status = rowStatus?.[item?.id];
            const hasUnsavedEdit = !!rowEdit;

            return (
              <tr key={item?.id} className={`border-b border-border hover:bg-muted/50 transition-smooth ${hasUnsavedEdit && !isEditing ? 'bg-amber-50/30' : ''}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selectedRows?.includes(item?.id)} onChange={() => handleSelectRow(item?.id)} className="w-4 h-4 rounded border-border" />
                </td>
                {/* Name */}
                <td className="px-4 py-3">
                  {isEditing ? (
                    <Input type="text" value={editData?.name ?? ''} onChange={(e) => setEditData({ ...editData, name: e?.target?.value })} className="w-full" />
                  ) : (
                    <input
                      type="text"
                      key={`name-${item?.id}-${item?.name}`}
                      defaultValue={displayItem?.name ?? ''}
                      onBlur={(e) => { if (e?.target?.value !== (item?.name ?? '')) handleFieldChange(item, 'name', e?.target?.value); }}
                      className="w-full text-sm text-foreground font-medium bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors"
                    />
                  )}
                </td>
                {/* Role */}
                <td className="px-4 py-3">
                  {isEditing ? (
                    <Select options={roleOptions} value={editData?.role} onChange={(value) => setEditData({ ...editData, role: value })} />
                  ) : (
                    <select
                      value={displayItem?.role ?? ''}
                      onChange={(e) => handleFieldChange(item, 'role', e?.target?.value)}
                      className="text-sm text-muted-foreground capitalize bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors w-full"
                    >
                      {roleOptions?.map(opt => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
                    </select>
                  )}
                </td>
                {/* Specialization */}
                <td className="px-4 py-3">
                  {isEditing ? (
                    <Input type="text" value={editData?.specialization ?? ''} onChange={(e) => setEditData({ ...editData, specialization: e?.target?.value })} className="w-full" />
                  ) : (
                    <input
                      type="text"
                      key={`spec-${item?.id}-${item?.specialization}`}
                      defaultValue={displayItem?.specialization ?? ''}
                      onBlur={(e) => { if (e?.target?.value !== (item?.specialization ?? '')) handleFieldChange(item, 'specialization', e?.target?.value); }}
                      className="w-full text-sm text-muted-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors"
                    />
                  )}
                </td>
                {/* Nationality */}
                <td className="px-4 py-3">
                  {isEditing ? (
                    <Select options={nationalityOptions} value={editData?.nationality || 'expat'} onChange={handleNationalityChange} />
                  ) : (
                    <select
                      value={displayItem?.nationality || 'expat'}
                      onChange={(e) => handleFieldChange(item, 'nationality', e?.target?.value)}
                      className={`text-sm font-medium bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors w-full ${nationality === 'omani' ? 'text-green-600' : 'text-muted-foreground'}`}
                    >
                      {nationalityOptions?.map(opt => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
                    </select>
                  )}
                </td>
                {/* Monthly Salary */}
                <td className="px-4 py-3 text-right">
                  {isEditing ? (
                    <Input type="number" value={editData?.monthlySalary ?? ''} onChange={(e) => handleMonthlySalaryChange(e?.target?.value)} className="w-full text-right" placeholder="0" />
                  ) : (
                    <input
                      type="number"
                      key={`salary-${item?.id}-${item?.monthlySalary}`}
                      defaultValue={displayItem?.monthlySalary ?? ''}
                      onBlur={(e) => { const nv = parseFloat(e?.target?.value) || 0; const ov = parseFloat(item?.monthlySalary) || 0; if (nv !== ov) handleFieldChange(item, 'monthlySalary', e?.target?.value); }}
                      className="w-full text-sm text-foreground font-medium text-right bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors"
                      placeholder="0"
                    />
                  )}
                </td>
                {/* Daily Salary */}
                <td className="px-4 py-3 text-right">
                  <span className="text-sm text-primary font-medium">{isEditing ? formatCurrency(editDailySalary) : formatCurrency(dailySalary)}</span>
                </td>
                {/* Hourly Salary */}
                <td className="px-4 py-3 text-right">
                  <span className="text-sm text-accent font-medium">{isEditing ? formatCurrency(editHourlySalary) : formatCurrency(hourlySalary)}</span>
                </td>
                {/* SPF % */}
                <td className="px-4 py-3 text-right">
                  {isEditing ? (
                    editNationality === 'omani' ? (
                      <Input type="number" value={editData?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE} onChange={(e) => handleSPFPercentageChange(e?.target?.value)} className="w-full text-right" placeholder="22.5" />
                    ) : <span className="text-sm text-muted-foreground">—</span>
                  ) : (
                    nationality === 'omani' ? (
                      <input
                        type="number"
                        key={`spf-${item?.id}-${item?.spfPercentage}`}
                        defaultValue={displayItem?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE}
                        onBlur={(e) => { const nv = parseFloat(e?.target?.value); const ov = parseFloat(item?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE); if (nv !== ov) handleFieldChange(item, 'spfPercentage', e?.target?.value); }}
                        className="w-full text-sm text-foreground text-right bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors"
                      />
                    ) : <span className="text-sm text-muted-foreground">—</span>
                  )}
                </td>
                {/* SPF Value */}
                <td className="px-4 py-3 text-right">
                  {isEditing ? (
                    editNationality === 'omani' ? <span className="text-sm text-orange-600 font-medium">{formatCurrency(editSpfValue)}</span> : <span className="text-sm text-muted-foreground">—</span>
                  ) : (
                    nationality === 'omani' ? <span className="text-sm text-orange-600 font-medium">{formatCurrency(spfValue)}</span> : <span className="text-sm text-muted-foreground">—</span>
                  )}
                </td>
                {/* Health Insurance Toggle */}
                <td className="px-4 py-3 text-center">
                  {isEditing ? (
                    <input type="checkbox" checked={editData?.healthInsuranceEnabled ?? false} onChange={(e) => setEditData({ ...editData, healthInsuranceEnabled: e?.target?.checked })} className="w-4 h-4 rounded border-border accent-primary" />
                  ) : (
                    <input type="checkbox" checked={displayItem?.healthInsuranceEnabled ?? false} onChange={(e) => handleFieldChange(item, 'healthInsuranceEnabled', e?.target?.checked)} className="w-4 h-4 rounded border-border accent-primary cursor-pointer" />
                  )}
                </td>
                {/* Health Insurance Value */}
                <td className="px-4 py-3 text-right">
                  {isEditing ? (
                    editData?.healthInsuranceEnabled ? (
                      <Input type="number" value={editData?.healthInsuranceValue ?? ''} onChange={(e) => setEditData({ ...editData, healthInsuranceValue: parseFloat(e?.target?.value) || 0 })} className="w-full text-right" placeholder="0" />
                    ) : <span className="text-sm text-muted-foreground">—</span>
                  ) : (
                    healthInsuranceEnabled ? (
                      <input
                        type="number"
                        key={`hi-${item?.id}-${item?.healthInsuranceValue}`}
                        defaultValue={displayItem?.healthInsuranceValue ?? ''}
                        onBlur={(e) => { const nv = parseFloat(e?.target?.value) || 0; const ov = parseFloat(item?.healthInsuranceValue) || 0; if (nv !== ov) handleFieldChange(item, 'healthInsuranceValue', nv); }}
                        className="w-full text-sm text-blue-600 font-medium text-right bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors"
                        placeholder="0"
                      />
                    ) : <span className="text-sm text-muted-foreground">—</span>
                  )}
                </td>
                {/* Utilization */}
                <td className="px-4 py-3 text-right">
                  {isEditing ? (
                    <Input type="number" value={editData?.utilizationDefault ?? ''} onChange={(e) => setEditData({ ...editData, utilizationDefault: parseFloat(e?.target?.value) || 0 })} className="w-full text-right" />
                  ) : (
                    <input
                      type="number"
                      key={`util-${item?.id}-${item?.utilizationDefault}`}
                      defaultValue={displayItem?.utilizationDefault ?? ''}
                      onBlur={(e) => { const nv = parseFloat(e?.target?.value) || 0; const ov = parseFloat(item?.utilizationDefault) || 0; if (nv !== ov) handleFieldChange(item, 'utilizationDefault', nv); }}
                      className="w-full text-sm text-foreground text-right bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 transition-colors"
                    />
                  )}
                </td>
                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center justify-center gap-2">
                      {isEditing ? (
                        <>
                          <Button variant="success" size="xs" iconName="Check" onClick={handleSave} />
                          <Button variant="ghost" size="xs" iconName="X" onClick={handleCancel} />
                        </>
                      ) : (
                        <>
                          <Button variant="ghost" size="xs" iconName="Edit" onClick={() => handleEdit(item)} />
                          <Button variant="ghost" size="xs" iconName="Trash2" onClick={() => onDelete(item?.id)} />
                          {status === 'failed' && (
                            <button onClick={() => handleRetry(item?.id)} className="text-xs text-red-600 hover:text-red-700 underline" title="Retry save">Retry</button>
                          )}
                        </>
                      )}
                    </div>
                    {!isEditing && <SaveStatusBadge status={status} />}
                  </div>
                </td>
              </tr>
            );
          })}
          {filteredData?.length === 0 && (
            <tr>
              <td colSpan={14} className="px-4 py-12 text-center text-sm text-muted-foreground">
                No outsource resources found. Add your first resource using the "Add New" button.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
});

OutsourceResourcesGrid.displayName = 'OutsourceResourcesGrid';

export default OutsourceResourcesGrid;
